import React from "react";
import produce from "immer";
import startCase from "lodash/startCase";
import { useDispatch, useSelector } from "react-redux";
import { TextFieldProps, Alert, Autocomplete, Button, TextField } from "@mui/material";

import { RootState } from "../../../store";
import { t } from "../../../components/translate";
import Modal from "../../../components/Modal/Modal";
import { resourceCentreActions } from "../../../actions";
import { getAllChildGlWithLedgerTypeFilter } from "../../accounts/hooks";
import { ChildGeneralLedger, LedgerType } from "../../../interfaces/Accounts";
import LedgerSelect from "../../ResourceCentre/Settings/AccountSettings/LedgerSelect";
import { paymentOptions, paymentOptionsEnum } from "./BillSummary";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";

interface PaymentMethodSelectProps {
  isCreditNote?: boolean;
  selectedPaymentMethod: string;
  onPaymentMethodChange: (e: React.SyntheticEvent, v: paymentOptionsEnum | string | null) => void;
  TxtFieldProps?: TextFieldProps;
  paymentOptionsProp?: Array<string>;
  disabled?: boolean;
  sxProp?: Record<string, unknown>;
  disablePortal?: boolean;
}

export default function PaymentMethodSelect({
  isCreditNote = false,
  selectedPaymentMethod,
  onPaymentMethodChange,
  TxtFieldProps,
  paymentOptionsProp,
  disabled = false,
  sxProp = { width: "300px" },
  disablePortal = false
}: PaymentMethodSelectProps): React.ReactNode {
  const dispatch = useDispatch();
  const resourceCentre = useCurrentResourceCentre();

  const { isAccountSubscribed, paymentMethods } = useSelector((state: RootState) => ({
    isAccountSubscribed: state.subscriptions.currentSubscription?.features?.account?.subscribed,
    paymentMethods: state.resources.resourceCentres[0]?.settings?.accountSettings?.modeOfPayment
  }));

  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [showLedgerSelect, setShowLedgerSelect] = React.useState<boolean>(false);
  const [selectedLedger, setSelectedLedger] = React.useState<ChildGeneralLedger | null>(null);

  const cashAndBankLedgers = getAllChildGlWithLedgerTypeFilter([
    LedgerType.BANK_LEDGER,
    LedgerType.CASH_LEDGER
  ]);

  // effect
  React.useEffect(() => {
    if (
      isAccountSubscribed &&
      paymentMethods &&
      !paymentMethods[selectedPaymentMethod]?.ledgerId &&
      ![paymentOptionsEnum.balance, paymentOptionsEnum.noPayment].includes(
        selectedPaymentMethod as paymentOptionsEnum
      )
    ) {
      setShowAlert(true);
    } else {
      setShowAlert(false);
    }
  }, [selectedPaymentMethod, isAccountSubscribed, paymentMethods]);

  return (
    <>
      <Autocomplete
        value={selectedPaymentMethod}
        options={
          paymentOptionsProp ||
          (isCreditNote
            ? paymentOptions
            : paymentOptions.filter((option) => option !== paymentOptionsEnum.noPayment))
        }
        disabled={disabled}
        disablePortal={disablePortal}
        slotProps={{
          popper: { sx: { zIndex: 9999 } }
        }}
        getOptionLabel={(option) => startCase(option)}
        sx={{ ...sxProp }}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line  react/jsx-props-no-spreading
            {...TxtFieldProps}
            // eslint-disable-next-line  react/jsx-props-no-spreading
            {...params}
            data-testmation="paymentMethodSelect"
            fullWidth
            variant="outlined"
            margin="none"
            placeholder={t("billing.selectPaymentMethod")}
          />
        )}
        onChange={onPaymentMethodChange}
      />

      {/* alert */}
      {showAlert && (
        <Alert
          action={
            <Button
              onClick={() => setShowLedgerSelect(true)}
              variant="outlined"
              color="inherit"
              size="small"
            >
              Map Ledger
            </Button>
          }
          severity="warning"
          sx={{ padding: "0px" }}
        >
          Not mapped to ledger
        </Alert>
      )}

      {/* modal */}
      <Modal
        open={showLedgerSelect}
        title="Select Ledger for Supplier"
        footer={
          <>
            <Button
              onClick={() => {
                setShowLedgerSelect(false);
                setSelectedLedger(null);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedLedger}
              onClick={() => {
                if (selectedLedger) {
                  dispatch(
                    resourceCentreActions.putResourceCentre(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      produce(resourceCentre, (draft: any) => {
                        const { modeOfPayment } = draft.settings.accountSettings;
                        if (modeOfPayment[selectedPaymentMethod]) {
                          draft.settings.accountSettings.modeOfPayment[
                            selectedPaymentMethod
                          ].ledgerId = selectedLedger.id;
                        } else {
                          draft.settings.accountSettings.modeOfPayment[selectedPaymentMethod] = {
                            ledgerId: selectedLedger.id
                          };
                        }
                      })
                    )
                  );
                  setSelectedLedger(null);
                  setShowLedgerSelect(false);
                  setShowAlert(false);
                }
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <LedgerSelect
          options={cashAndBankLedgers || []}
          selected={selectedLedger}
          onChange={(value) => setSelectedLedger(value)}
        />
      </Modal>
    </>
  );
}
